import React from 'react';
import type { BasicInfo, ValidationErrors } from '../../../types/wizard';
import { US_STATES } from '@shared/constants/us-data';
import '../ClinicCreationWizard.css';

interface BasicInfoFormProps {
  values: BasicInfo;
  onChange: (data: Partial<BasicInfo>) => void;
  errors: ValidationErrors;
  /**
   * Hide the address block (SLV2-2821). The verification wizard shows addresses on
   * its own "Verify Addresses" step — rendering them here too duplicated the fields,
   * and edits made here were silently dropped (verification saves addresses from
   * mailingAddress state, not basicInfo). The Add-Clinic wizard keeps the default.
   */
  showAddress?: boolean;
  /**
   * Hide the Clinic Code field (SLV2-2819 meeting item, Jared 2026-08-24): the
   * create wizard no longer collects a code — usp_CreateClinic assigns the next
   * sequential one server-side. The verification wizard keeps the field so an
   * existing clinic's code stays visible and editable.
   */
  showCode?: boolean;
}

const BasicInfoForm: React.FC<Readonly<BasicInfoFormProps>> = ({
  values,
  onChange,
  errors,
  showAddress = true,
  showCode = true,
}) => {
  return (
    <>
      {/* Clinic Identity */}
      <div className="form-row">
        <div className="form-group">
          <label htmlFor="name">
            Clinic Name <span className="required">*</span>
          </label>
          <input
            type="text"
            id="name"
            value={values.name}
            onChange={(e) => onChange({ name: e.target.value })}
            className={errors.name ? 'error' : ''}
            placeholder="Enter clinic name"
          />
          {errors.name && <div className="error-text">{errors.name}</div>}
        </div>

        {showCode && (
          <div className="form-group">
            <label htmlFor="code">Clinic Code</label>
            <input
              type="text"
              id="code"
              value={values.code || ''}
              onChange={(e) => onChange({ code: e.target.value })}
              className={errors.code ? 'error' : ''}
              placeholder="Enter clinic code"
            />
            {errors.code && <div className="error-text">{errors.code}</div>}
          </div>
        )}
      </div>

      {/* Address */}
      {showAddress && (
        <>
      <div className="form-group form-section-divider">
        <h3 className="form-section-heading">
          Address
        </h3>
      </div>

      <div className="form-group">
        <label htmlFor="address1">
          Address Line 1 <span className="required">*</span>
        </label>
        <input
          type="text"
          id="address1"
          value={values.address1 || ''}
          onChange={(e) => onChange({ address1: e.target.value })}
          className={errors.address1 ? 'error' : ''}
          placeholder="Enter street address"
        />
        {errors.address1 && <div className="error-text">{errors.address1}</div>}
      </div>

      <div className="form-group">
        <label htmlFor="address2">Address Line 2</label>
        <input
          type="text"
          id="address2"
          value={values.address2 || ''}
          onChange={(e) => onChange({ address2: e.target.value })}
          placeholder="Apartment, suite, etc. (optional)"
        />
      </div>

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="city">
            City <span className="required">*</span>
          </label>
          <input
            type="text"
            id="city"
            value={values.city || ''}
            onChange={(e) => onChange({ city: e.target.value })}
            className={errors.city ? 'error' : ''}
            placeholder="Enter city"
          />
          {errors.city && <div className="error-text">{errors.city}</div>}
        </div>

        <div className="form-group">
          <label htmlFor="state">
            State <span className="required">*</span>
          </label>
          <select
            id="state"
            value={values.state || ''}
            onChange={(e) => onChange({ state: e.target.value })}
            className={errors.state ? 'error' : ''}
          >
            <option value="">Select State</option>
            {US_STATES.map((stateOption) => (
              <option key={stateOption.value} value={stateOption.value}>
                {stateOption.label}
              </option>
            ))}
          </select>
          {errors.state && <div className="error-text">{errors.state}</div>}
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="zip">
            ZIP Code <span className="required">*</span>
          </label>
          <input
            type="text"
            id="zip"
            value={values.zip || ''}
            onChange={(e) => onChange({ zip: e.target.value })}
            className={errors.zip ? 'error' : ''}
            placeholder="12345"
            maxLength={10}
          />
          {errors.zip && <div className="error-text">{errors.zip}</div>}
        </div>

        <div className="form-group">
          <label htmlFor="country">Country</label>
          <input
            type="text"
            id="country"
            value={values.country || 'US'}
            readOnly
            disabled
          />
        </div>
      </div>
        </>
      )}

      {/* Contact Details */}
      <div className="form-group form-section-divider">
        <h3 className="form-section-heading">
          Contact Details
        </h3>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="phone">Phone</label>
          <input
            type="tel"
            id="phone"
            value={values.phone || ''}
            onChange={(e) => onChange({ phone: e.target.value })}
            placeholder="(123) 456-7890"
          />
        </div>

        <div className="form-group">
          <label htmlFor="fax">Fax</label>
          <input
            type="tel"
            id="fax"
            value={values.fax || ''}
            onChange={(e) => onChange({ fax: e.target.value })}
            placeholder="(123) 456-7890"
          />
        </div>
      </div>

      <div className="form-group">
        <label htmlFor="email">Email</label>
        <input
          type="email"
          id="email"
          value={values.email || ''}
          onChange={(e) => onChange({ email: e.target.value })}
          className={errors.email ? 'error' : ''}
          placeholder="clinic@example.com"
        />
        {errors.email && <div className="error-text">{errors.email}</div>}
      </div>

      <div className="form-group">
        <label htmlFor="website">Website</label>
        <input
          type="text"
          id="website"
          value={values.website || ''}
          onChange={(e) => onChange({ website: e.target.value })}
          placeholder="example.com"
        />
      </div>
    </>
  );
};

export default BasicInfoForm;
